<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { MAX_PREPARATION_DURATION_MINUTES } from "../../../../_shared/_logic/work-order-creation"
  import { SCHEDULING_MINUTE_STEP } from "../../../../../../../../../../../../../../_shared/_logic/time-granularity"
  import { formatDateOnly } from "../../../../../../../../../../../../_shared/_logic/dates"
  import {
    CUSTOM_ORDER_TYPE_SELECTION,
    catalogOrderTypeIds,
    isCustomOrderTypeSelection,
    selectedOrderTypeItems,
  } from "../../../../../../../../../../../../_shared/_logic/order-type-selection"
  import {
    reconcileKeepPreconfigurationSelection,
    withKeepPreconfigurationOption,
  } from "../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"
  import { planningDueDatePresetOptions } from "../../../../_shared/_logic/planning-due-date-preset"
  import {
    CUSTOM_DURATION_SELECTION,
    changeDueDatePreconfiguration,
    changeOrderTypePreconfiguration,
    durationPreconfigurationOptions,
    type OrderFieldsPreconfiguration,
  } from "./_logic/orderFieldsPreconfiguration"

  let {
    value = $bindable(),
    orderTypes,
    employeeOptions,
    disabled = false,
    brightBackground = true,
    timezone,
  }: {
    value: OrderFieldsPreconfiguration
    orderTypes: OrderType[]
    employeeOptions: Array<{ label: string; value: string }>
    brightBackground?: boolean
    disabled?: boolean
    timezone?: string
  } = $props()
  const id = $props.id()
  const now = new Date()
  const dueDatePresets = $derived(planningDueDatePresetOptions(now, timezone))
  const surfaceTone = $derived(brightBackground ? "light" : "dark")
  const orderTypeOptions = $derived(
    withKeepPreconfigurationOption([
      ...[...orderTypes]
        .sort((a, b) =>
          a.internal_label.localeCompare(b.internal_label, "de", { sensitivity: "base" }),
        )
        .map((type) => ({ label: type.internal_label, value: type.id })),
      { label: "Sonstige (freier Text)", value: CUSTOM_ORDER_TYPE_SELECTION },
    ]),
  )
  const employeeSelectOptions = $derived(withKeepPreconfigurationOption(employeeOptions))
  const dueDateOptions = $derived(withKeepPreconfigurationOption(dueDatePresets))
  const instructionSourceLabel = $derived.by(() => {
    const ids = catalogOrderTypeIds(value.orderTypeValues)

    if (ids.length < 2) return null

    return (
      selectedOrderTypeItems({ order_type_ids: ids, custom_order_type: null }, orderTypes)[0]
        ?.internal_label ?? null
    )
  })
  const valuesFrom = (next: unknown) =>
    Array.isArray(next) ? next.map(String) : next ? [String(next)] : []
</script>

<div class="creation-fields form light">
  <div class="row fields-row">
    <Input
      id={`${id}-type`}
      style="width: 100%; min-width: 0"
      type="select"
      multiple={true}
      clearable={false}
      placeholder="Auftragstyp wählen"
      options={orderTypeOptions}
      bind:value={
        () => value.orderTypeValues,
        (next) => (value = changeOrderTypePreconfiguration(value, valuesFrom(next), orderTypes))
      }
      tooltip="Bestimmt die Bezeichnung und die vorgeschlagene Dauer. Auftragstypen verwalten Sie in den Einstellungen."
      {disabled}
      {brightBackground}
      {surfaceTone}
    />
    {#if isCustomOrderTypeSelection(value.orderTypeValues)}
      <Input
        id={`${id}-type-custom`}
        style="width: 100%; min-width: 0"
        placeholder="Auftragstyp (freier Text)"
        tooltip="Bezeichnung für den benutzerdefinierten Auftragstyp."
        bind:value={
          () => value.customOrderTypeLabel,
          (next) => (value = { ...value, customOrderTypeLabel: String(next ?? "") })
        }
        {disabled}
        {brightBackground}
        {surfaceTone}
      />
    {/if}
    <Input
      id={`${id}-duration`}
      style="width: 100%; min-width: 0"
      type="select"
      clearable={false}
      placeholder="Dauer"
      options={durationPreconfigurationOptions}
      bind:value={
        () => value.duration, (next) => (value = { ...value, duration: String(next ?? "") })
      }
      tooltip="Standarddauer für die Termine dieses Auftrags. Ein neuer Auftragstyp schlägt seine typische Dauer vor."
      {disabled}
      {brightBackground}
      {surfaceTone}
    />
    {#if value.duration === CUSTOM_DURATION_SELECTION}
      <Input
        id={`${id}-duration-custom`}
        style="width: 100%; min-width: 0"
        type="number"
        placeholder="Individuelle Dauer (Minuten)"
        min={SCHEDULING_MINUTE_STEP}
        max={MAX_PREPARATION_DURATION_MINUTES}
        step={SCHEDULING_MINUTE_STEP}
        bind:value={
          () => value.customDurationMinutes,
          (next) => (value = { ...value, customDurationMinutes: String(next ?? "") })
        }
        {disabled}
        {brightBackground}
        {surfaceTone}
      />
    {/if}
    <Input
      id={`${id}-employees`}
      style="width: 100%; min-width: 0"
      type="select"
      multiple={true}
      clearable={false}
      placeholder="Zugelassene Mitarbeitende"
      options={employeeSelectOptions}
      bind:value={
        () => value.allowedEmployeeIds,
        (next) =>
          (value = {
            ...value,
            allowedEmployeeIds: reconcileKeepPreconfigurationSelection(
              value.allowedEmployeeIds,
              valuesFrom(next),
            ),
          })
      }
      tooltip="Diese Mitarbeitenden dürfen für diesen Auftrag eingesetzt werden, auch bei späteren Terminverschiebungen."
      {disabled}
      {brightBackground}
      {surfaceTone}
    />
    <Input
      id={`${id}-due`}
      style="width: 100%; min-width: 0"
      type="select"
      clearable={false}
      placeholder="Auftragsfrist"
      options={dueDateOptions}
      bind:value={
        () => value.dueDatePreset,
        (next) => (value = changeDueDatePreconfiguration(value, String(next ?? ""), dueDatePresets))
      }
      tooltip="Bis zu diesem Datum soll der Auftrag erledigt werden. Die Frist gehört zum Auftrag und bleibt beim späteren Planen erhalten."
      {disabled}
      {brightBackground}
      {surfaceTone}
    />
    {#if value.dueDatePreset === "custom"}
      <Input
        id={`${id}-due-custom`}
        style="width: 100%; min-width: 0"
        type="date"
        placeholder="Individuelle Auftragsfrist"
        bind:value={
          () => value.dueDate, (next) => (value = { ...value, dueDate: formatDateOnly(next) })
        }
        {disabled}
        {brightBackground}
        {surfaceTone}
      />
    {/if}
  </div>

</div>

<style>
  .creation-fields {
    width: 100%;
    min-width: 0;
    container-type: inline-size;
    padding-top: 0.75rem;
  }
  .creation-fields :global(.row) {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
  .creation-fields :global(.row > *) {
    width: 100%;
    min-width: 0;
  }
  .order-type-hint {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    line-height: 1.4;
    opacity: 0.72;
  }
  @container (max-width:420px) {
    .creation-fields :global(.row) {
      grid-template-columns: 1fr;
    }
  }
</style>
